import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ProtectedImage } from "@/components/ProtectedImage";
import { useAuth } from "@/context/AuthContext";
import { TenantBrand } from "@/components/TenantBrand";
import { Brand } from "@/components/Brand";
import { num, brl, locString } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ScanLine, Search, ClipboardList, LayoutDashboard, LogOut, Package, X, MapPin, Wrench,
} from "lucide-react";

const Shortcut = ({ icon: Icon, label, color, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 h-16 md:landscape:h-14 transition-transform active:scale-95 ${color}`}>
    <Icon className="h-5 w-5 shrink-0" />
    <span className="font-display text-sm font-bold text-center leading-tight">{label}</span>
  </button>
);

export default function OperationalHome() {
  const navigate = useNavigate();
  const { user, logout, hasModule } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    if (!hasModule("estoque")) { setResults(null); return; }
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get("/parts", { params: { q, page: 1, page_size: 20 } }).then((r) => setResults(r.data.items)).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q, hasModule]);

  return (
    <div className="min-h-screen almox-bg text-slate-100">
      <header className="glass border-b border-slate-800 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        {user?.is_platform_admin ? <Brand size="sm" /> : <TenantBrand user={user} size="sm" />}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-slate-300"><LayoutDashboard className="h-4 w-4 mr-1" /> Painel</Button>
          <Button variant="ghost" size="icon" onClick={logout} className="text-slate-400"><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <div className="max-w-xl md:landscape:max-w-5xl mx-auto p-4 sm:p-6 space-y-6 md:landscape:space-y-4">
        <div className="text-center">
          <h1 className="font-display text-3xl md:landscape:text-2xl font-bold text-slate-50">Operação do Almoxarifado</h1>
          <p className="text-slate-400 text-sm">Olá, {user?.name}. Busque a peça para realizar a operação.</p>
        </div>

        {hasModule("estoque") && (
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input ref={searchRef} data-testid="input-op-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar peça por nome, código, máquina..." className="pl-12 h-14 md:landscape:h-12 text-base bg-slate-900/70 border-slate-700" />
            {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"><X className="h-5 w-5" /></button>}
          </div>
        )}

        {hasModule("estoque") && results !== null ? (
          <div className="space-y-2">
            {searching && <p className="text-sm text-slate-500 text-center">Buscando...</p>}
            {results.map((p) => (
              <button key={p.id} data-testid={`op-result-${p.id}`} onClick={() => navigate(`/pecas/${p.id}`)}
                className="w-full flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-3 text-left active:scale-[0.99] transition-transform">
                <div className="h-14 w-14 rounded-lg bg-slate-950 overflow-hidden shrink-0">{p.foto_principal ? <ProtectedImage src={p.foto_principal} alt="" className="h-full w-full object-cover" /> : <Package className="h-6 w-6 m-4 text-slate-700" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2"><span className="font-semibold truncate">{p.descricao}</span><StatusBadge status={p.metrics?.status_reposicao} /></div>
                  <div className="font-mono text-xs text-sky-400">{p.codigo_interno} · {num(p.quantidade_atual)} {p.unidade}</div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{locString(p.localizacao)}</div>
                </div>
              </button>
            ))}
            {!searching && results.length === 0 && <p className="text-center text-slate-500 py-6">Nenhuma peça encontrada.</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {hasModule("estoque") && <Shortcut testid="op-tile-scan" icon={ScanLine} label="Escanear QR Code" color="bg-sky-800/40 border-sky-700/50 text-sky-100" onClick={() => navigate("/operacional/scan")} />}
            {hasModule("manutencao") && <Shortcut testid="op-tile-maintenance" icon={Wrench} label="Pendências Manutenção" color="bg-indigo-900/40 border-indigo-800/50 text-indigo-100" onClick={() => navigate("/manutencao-pecas")} />}
            {hasModule("inventario") && <Shortcut testid="op-tile-inventory" icon={ClipboardList} label="Inventário" color="bg-amber-900/40 border-amber-800/50 text-amber-100" onClick={() => navigate("/inventario")} />}
            {!hasModule("estoque") && !hasModule("manutencao") && !hasModule("inventario") && (
              <div className="sm:col-span-3 rounded-xl border border-slate-800 bg-slate-900/50 p-6 text-center text-slate-400">
                Nenhum módulo operacional está liberado para esta empresa.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
